import * as React from 'react';
import { InboxFolderStats } from '../Folders/types/InboxFolderStats';
import Folders from '../Folders/Folders';
import SearchInput from '../SearchInput/SearchInput';
import styled from 'styled-components';

export type InboxLayoutProps = {
    /** Index of the currently active folder in `inboxFolders`  */
    readonly activeFolder: number | null;
    readonly children: React.ReactNode;
    readonly className?: string;
    /** React component displayed to the right of inbox `children` */
    readonly context?: React.ReactNode;
    /** funtion for formatting numbers */
    readonly formatNumber?: Function;
    /** Toggles display of `context` component */
    readonly hideContext?: boolean;
    /** Toggles display of the left side navigation (folders and search) */
    readonly hideLeftNav?: boolean;
    /** Toggles display of search input in left nav */
    readonly hideSearch?: boolean;
    /** Toggles display of custom folders in left nav */
    readonly hideUserFolders?: boolean;
    /** `InboxFolderStats` config for folder structure */
    readonly inboxFolders: InboxFolderStats;
    /** Indicates that current folder is loading */
    readonly loading: boolean;
    /** Callback for archiving a user folder */
    readonly onArchiveFolder: Function;
    /** Callback for creating a new user folder */
    readonly onCreateFolder: Function;
    /** Callback function called when a folder is renamed */
    readonly onRenameFolder?: Function;
    /** Callback function called when text is typed into the search field */
    readonly onSearch?: Function;
    /** Callback function called when a folder is clicked */
    readonly onSetActiveInbox: Function;
    readonly testid?: string;
};

const InboxLayout = ({
    activeFolder,
    children,
    className,
    context,
    formatNumber = (n: number) => n,
    hideContext,
    hideLeftNav,
    hideSearch,
    hideUserFolders,
    inboxFolders,
    loading,
    onArchiveFolder,
    onCreateFolder,
    onRenameFolder = () => {},
    onSearch = () => {},
    onSetActiveInbox,
    testid = 'messengerInboxLayout',
}: InboxLayoutProps) => (
    <LayoutContainer
        className={className}
        data-testid={testid}
    >
        {hideLeftNav && (
            <SearchInput
                aria-label="Search Input"
                loading={loading}
                onSearch={onSearch}
            />
        )}
        <InnerLayout>
            {!hideLeftNav && (
                <LeftNavContainer>
                    {!hideSearch && (
                        <SearchInput
                            aria-label="Search Input"
                            loading={loading}
                            onSearch={onSearch}
                        />
                    )}
                    <Folders
                        activeFolder={activeFolder}
                        formatNumber={formatNumber}
                        hideUserFolders={hideUserFolders}
                        inboxFolders={inboxFolders}
                        loading={loading}
                        onArchiveFolder={onArchiveFolder}
                        onCreateFolder={onCreateFolder}
                        onRenameFolder={onRenameFolder}
                        onSetActiveInbox={onSetActiveInbox}
                    />
                </LeftNavContainer>
            )}
            {children}
            {!hideContext && context}
        </InnerLayout>
    </LayoutContainer>
);

export default InboxLayout;

const LeftNavContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 224px;
    flex: 0 0 224px;
    margin-right: 30px;

    @media print {
        display: none;
    }
`;

const LayoutContainer = styled.main`
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin: 0 auto 160px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    padding: 0;
`;

const InnerLayout = styled.div`
    display: flex;
    flex-direction: row;

    @media print {
        flex-direction: column-reverse;
    }
`;
